import ConfirmChangeLocationDialog from "@components/dialogs/ConfirmChangeLocationDialog.svelte";
import { places } from "@components/inputs/locationInput/dutchCitiesAndTowns";
import { useDialog } from "@components/utils/useDialog";
import { hits } from "@stores/hits";
import {
	coordinates,
	coordinatesPolygon,
	employmentTypes,
	lastQuery,
	locationQuery,
	query,
	queryID,
	salary,
	sectors
} from "@stores/searchQuery";
import type { IHitMetaData } from "@typesAndInterfaces/types";
import algoliasearch from "algoliasearch/lite";
import type { UUID } from "crypto";
import fuzzysort from "fuzzysort";
import aa from "search-insights";
import { get, writable } from "svelte/store";
import { supabase } from "../lib/supabase";

type IndexName = "Nutzy Staging" | "Nutzy Production" | "Nutzy Testing";

interface SearchOptions {
	insidePolygon: number[][];
	filters?: string;
	hitsPerPage?: number;
	distinct?: number;
	clickAnalytics?: boolean;
}

function generateNeedles(inputString: string) {
	const words = inputString.split(" ");
	const needles = [];

	// Generate all contiguous word combinations
	for (let i = 0; i < words.length; i++) {
		let currentCombo = "";
		for (let j = i; j < words.length; j++) {
			currentCombo += (j > i ? " " : "") + words[j];
			needles.push(currentCombo);
		}
	}

	return needles;
}

export function algoliaFireUserAppliedEvent(params: {
	objectID: string;
	afterSearch?: boolean;
}) {
	try {
		if (params?.afterSearch) {
			aa("convertedObjectIDsAfterSearch", {
				index: import.meta.env.PUBLIC_ALGOLIA_INDEX_NAME,
				queryID: get(queryID),
				eventName: "User Applied",
				objectIDs: [params.objectID]
			});
		} else {
			aa("convertedObjectIDs", {
				index: import.meta.env.PUBLIC_ALGOLIA_INDEX_NAME,
				eventName: "User Applied",
				objectIDs: [params.objectID]
			});
		}
	} catch (err) {
		console.error("Function algoliaFireUserAppliedEvent failed:", err);
	}
}

export function algoliaFireNavigateToFanpageEvent(params: {
	objectID: string;
	afterSearch?: {
		positions: number;
	};
}) {
	try {
		if (params?.afterSearch && params?.afterSearch.positions > -1) {
			aa("clickedObjectIDsAfterSearch", {
				index: import.meta.env.PUBLIC_ALGOLIA_INDEX_NAME,
				eventName: "Fanpage Navigated",
				objectIDs: [params.objectID],
				queryID: get(queryID),
				positions: [params.afterSearch.positions + 1]
			});
		} else {
			aa("clickedObjectIDs", {
				index: import.meta.env.PUBLIC_ALGOLIA_INDEX_NAME,
				eventName: "Fanpage Navigated",
				objectIDs: [params.objectID]
			});
		}
	} catch (err) {
		console.error("Function algoliaFireNavigateToFanpageEvent failed:", err);
	}
}

export function algoliaFireJobOfferLikedEvent(params: {
	objectID: string;
	afterSearch?: {
		positions: number;
	};
}) {
	try {
		if (params?.afterSearch && params?.afterSearch.positions > -1) {
			aa("clickedObjectIDsAfterSearch", {
				index: import.meta.env.PUBLIC_ALGOLIA_INDEX_NAME,
				eventName: "Job Offer Liked",
				objectIDs: [params.objectID],
				queryID: get(queryID),
				positions: [params.afterSearch.positions + 1]
			});
		} else {
			aa("clickedObjectIDs", {
				index: import.meta.env.PUBLIC_ALGOLIA_INDEX_NAME,
				eventName: "Job Offer Liked",
				objectIDs: [params.objectID]
			});
		}
	} catch (err) {
		console.error("Function algoliaFireJobOfferLikedEvent failed:", err);
	}
}

export function algoliaFireJobOfferSharedEvent(params: {
	objectID: string;
	afterSearch?: {
		positions: number;
	};
}) {
	try {
		if (params?.afterSearch && params?.afterSearch.positions > -1) {
			aa("clickedObjectIDsAfterSearch", {
				index: import.meta.env.PUBLIC_ALGOLIA_INDEX_NAME,
				eventName: "Job Offer Shared",
				objectIDs: [params.objectID],
				queryID: get(queryID),
				positions: [params.afterSearch.positions + 1]
			});
		} else {
			aa("clickedObjectIDs", {
				index: import.meta.env.PUBLIC_ALGOLIA_INDEX_NAME,
				eventName: "Job Offer Shared",
				objectIDs: [params.objectID]
			});
		}
	} catch (err) {
		console.error("Function algoliaFireJobOfferSharedEvent failed:", err);
	}
}

export const useAlgoliaClient = (function () {
	const loading = writable(true);

	const client = algoliasearch(
		import.meta.env.PUBLIC_ALGOLIA_APP_ID,
		import.meta.env.PUBLIC_ALGOLIA_SEARCH_API_KEY
	);

	const fanpageIndex = writable(
		client.initIndex(import.meta.env.PUBLIC_ALGOLIA_INDEX_NAME)
	);

	// Perform search based on the current query and filters. Optionally, a distinct value can be passed to the function, reducing the number of jobs per company.
	const performSearch = async ({
		distinct = 0,
		keepOldHits = false,
		overwriteQuery = "",
		checkForLocationInQueryString = true,
		hitsPerPage = 200
	} = {}) => {
		loading.set(true);
		// We need to go back to top for new results
		if (window) {
			window.scrollTo(0, 0);
		}

		const employmentTypes_ = get(employmentTypes);
		const jobSectorType_ = get(sectors);
		const polygon = get(coordinatesPolygon);
		let query_ = overwriteQuery ? overwriteQuery : get(query);
		const salary_ = get(salary);

		if (checkForLocationInQueryString) {
			const needles = generateNeedles(query_);

			type SearchResult = {
				result: any;
				needle: string;
				score: number;
			};

			let highestResult: SearchResult | null = null;

			needles.forEach((needle) => {
				const foundPlaces = fuzzysort.go(needle, places, {
					threshold: 1,
					limit: 1,
					keys: [(obj) => obj[0]]
				});

				if (foundPlaces.length > 0) {
					const currentResult: SearchResult = {
						result: foundPlaces[0].obj,
						needle: needle,
						score: foundPlaces[0].score
					};

					if (
						!highestResult ||
						highestResult.score < currentResult.score ||
						highestResult.needle.length < needle.length
					) {
						highestResult = currentResult;
					}
				}
			});

			// Type guard to ensure highestResult is not null
			if (
				highestResult !== null &&
				!get(locationQuery)
					.toLocaleLowerCase()
					.includes((highestResult as SearchResult).needle.toLocaleLowerCase())
			) {
				const { open } = useDialog();
				open(ConfirmChangeLocationDialog, {
					result: (highestResult as SearchResult).result,
					needle: (highestResult as SearchResult).needle
				});
				loading.set(false);

				return;
			}
		}

		// All jobs that are remote are located on a location close to the northpole. Not exactly the northpole because this leads to trouble with a polygon crossing the 180th meridian.
		// This polygon is a triangle inside of which this location is located.
		const northpolePolygon = [90, 0, 88, 0, 88, 1];

		const searchOptions: SearchOptions = {
			insidePolygon: [northpolePolygon, polygon]
		};

		// Build filter parts separately, then combine them properly
		let filterParts = [];

		// Employment types filter
		if (employmentTypes_) {
			const checkedEmploymentTypes = employmentTypes_.filter(
				(employmentType: any) => employmentType.checked
			);

			if (checkedEmploymentTypes.length > 0) {
				const employmentTypeFilters = checkedEmploymentTypes.map(
					(employmentType: any) => `employmentType:${employmentType.searchTerm}`
				);

				// If multiple employment types, group with OR and add to filter parts
				if (employmentTypeFilters.length > 1) {
					filterParts.push(`(${employmentTypeFilters.join(" OR ")})`);
				} else {
					filterParts.push(employmentTypeFilters[0]);
				}
			}
		}

		// Sector filter
		if (jobSectorType_ && !query_) {
			const checkedSectors = jobSectorType_.filter(
				(sector: any) => sector.checked
			);

			if (checkedSectors.length > 0) {
				const sectorFilters = checkedSectors.map(
					(sector: any) => `sector:${sector.id}`
				);

				// If multiple sectors, group with OR and add to filter parts
				if (sectorFilters.length > 1) {
					filterParts.push(`(${sectorFilters.join(" OR ")})`);
				} else {
					filterParts.push(sectorFilters[0]);
				}
			}
		} else {
			// When someone searches, reset the sector filter
			sectors.update((s) => {
				s.map((sector) => {
					sector.checked = false;
				});
				return s;
			});
		}

		// Salary filter
		if (salary_ && salary_[0] > 0) {
			filterParts.push(`baseSalary >= ${salary_[0]}`);
		}

		// Combine all filter parts with AND
		if (filterParts.length > 0) {
			searchOptions.filters = filterParts.join(" AND ");
		}
		if (distinct) {
			searchOptions.distinct = distinct;
		}

		const client = get(fanpageIndex);

		searchOptions.hitsPerPage = hitsPerPage;
		// Adds queryID
		searchOptions.clickAnalytics = true;

		const data = await client.search<IHitMetaData>(query_, searchOptions);
		queryID.set(data.queryID as UUID);

		// KeepOldHits is used for jobOfferNotFound increase distance
		// It will search the query again but filter all hits that were already found to prevent duplicates
		if (keepOldHits) {
			const newHits = data.hits.filter((hit) => {
				return !get(hits).some((existingHit) => {
					return existingHit.objectID === hit.objectID;
				});
			});

			hits.set([...get(hits), ...newHits]);
		} else {
			hits.set(data.hits);
		}
		loading.set(false);
		lastQuery.set(query_);
	};

	// Fetch location coordinates based on a query
	const fetchLocation = async (query: string) => {
		loading.set(true);

		const { data, error } = await supabase
			.from("coordinates")
			.select()
			.textSearch("city", `'${query}'`);

		if (error) {
			console.error("Error fetching location", error);
			return null;
		}
		loading.set(false);

		if (data.length) {
			const coords = data[0].coordinates as [number, number];
			coordinates.set(coords); // Set the coordinates store
			return coords;
		}
	};

	return () => {
		return {
			fanpageIndex,
			setFanpageIndex: (string: IndexName) => {
				fanpageIndex.set(client.initIndex(string));
			},
			performSearch,
			fetchLocation,
			loading
		};
	};
})();
